import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPost, operationsClient } from '@/lib/api';
import { membershipEndpoints } from '@/lib/api/endpoints';
import { jobKeys } from '@/lib/queryKeys';

/** One pending visit in the Recurring Service Events queue (ServiceTitan). */
export interface RecurringServiceQueueRow {
  id: string;
  eventDate: string;
  membershipId: string;
  membershipRecurringServiceId: string;
  customerId: string;
  customerName: string | null;
  serviceLocationId: string | null;
  locationLabel: string | null;
  label: string | null;
  jobTypeId: string | null;
  businessUnitId: string | null;
  businessUnitName: string | null;
  preferredTechnicianId: string | null;
  preferredTechnicianName: string | null;
  durationType: 'FIXED' | 'CONTINUOUS' | null;
  totalVisits: number | null;
  visitsRemaining: number | null;
  coveredEquipmentIds: string[];
  coveredUnitCount: number;
  durationExtensionMinutes: number;
  /** ServiceTitan's Booking Availability — config-completeness, not tech slots. */
  bookable: boolean;
  notBookableReason: string | null;
  /** Proactive tune-up outreach state for this visit (null = Iris not enlisted). */
  outreachStatus: string | null;
  dismissedEventRevenuePolicy: 'ALWAYS_RECOGNIZE' | 'PROMPT' | null;
}

export interface RecurringServiceQueueParams {
  page?: number;
  limit?: number;
  customerId?: string;
  technicianId?: string;
  businessUnitId?: string;
  availability?: 'bookable' | 'not_bookable';
  outreach?: 'needs_office';
  search?: string;
  sortDir?: 'asc' | 'desc';
}

interface QueuePage {
  data: RecurringServiceQueueRow[];
  page: number;
  totalPages: number;
  count: number;
}

const queueKey = (params: RecurringServiceQueueParams) =>
  ['recurring-service-events', 'queue', params] as const;

/** The paged, filterable booking queue. Keeps the previous page while a new one
 * loads so paging/filtering doesn't flash an empty grid. */
export function useRecurringServiceQueue(
  params: RecurringServiceQueueParams,
  options: { enabled?: boolean } = {},
) {
  return useApiQuery<QueuePage>({
    queryKey: queueKey(params),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiGet<QueuePage>(operationsClient, membershipEndpoints.recurringServiceQueue(params)),
  });
}

function invalidateQueue(queryClient: ReturnType<typeof useQueryClient>) {
  // Booking/dismissing changes the queue and the entitlement/meter views alike.
  void queryClient.invalidateQueries({ queryKey: ['recurring-service-events'] });
}

/** Dismiss a single pending event (drops it from the queue, no job). */
export function useDismissRecurringServiceEvent() {
  const queryClient = useQueryClient();
  return useApiMutation<
    { id: string; status: string },
    { id: string; recognizeRevenue?: boolean }
  >({
    mutationFn: async ({ id, recognizeRevenue }) => {
      const res = await apiPost<
        { data: { id: string; status: string } },
        { recognizeRevenue?: boolean }
      >(
        operationsClient,
        membershipEndpoints.dismissRecurringServiceEvent(id),
        { recognizeRevenue },
      );
      return res.data;
    },
    onSuccess: () => invalidateQueue(queryClient),
  });
}

/** Bulk-dismiss the selected events. Booking several at once is a different
 * thing — one trip to one home — and goes through the book drawer. */
export function useBulkDismissRecurringServiceEvents() {
  const queryClient = useQueryClient();
  return useApiMutation<
    { dismissed: number },
    { ids: string[]; recognizeRevenue?: boolean }
  >({
    mutationFn: async ({ ids, recognizeRevenue }) => {
      const res = await apiPost<
        { data: { dismissed: number } },
        { ids: string[]; recognizeRevenue?: boolean }
      >(
        operationsClient,
        membershipEndpoints.bulkDismissRecurringServiceEvents(),
        { ids, recognizeRevenue },
      );
      return res.data;
    },
    onSuccess: () => invalidateQueue(queryClient),
  });
}

export interface AttachVisitResult {
  id: string;
  jobId: string;
  status: string;
  /** How much work the visit added to the job. */
  addedMinutes: number;
  /** How long the job now needs in total (null on a replay). */
  requiredMinutes: number | null;
}

/** Add a due visit to a job that already exists at the same home. The job's
 * appointment is not stretched; the job's own required time grows. */
export function useAttachRecurringServiceEvent() {
  const queryClient = useQueryClient();
  return useApiMutation<AttachVisitResult, { id: string; jobId: string }>({
    mutationFn: async ({ id, jobId }) => {
      const res = await apiPost<{ data: AttachVisitResult }, { jobId: string }>(
        operationsClient,
        membershipEndpoints.attachRecurringServiceEvent(id),
        { jobId },
      );
      return res.data;
    },
    onSuccess: (result) => {
      invalidateQueue(queryClient);
      void queryClient.invalidateQueries({ queryKey: jobKeys.detail(result.jobId) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.appointments(result.jobId) });
    },
  });
}

export interface DetachVisitResult {
  id: string;
  jobId: string;
  status: string;
  /** How much work came off the job with the visit. */
  removedMinutes: number;
}

/** Take an added visit back off a job: it returns to the queue, and its $0
 * line, note and time come off the job. */
export function useDetachRecurringServiceEvent() {
  const queryClient = useQueryClient();
  return useApiMutation<DetachVisitResult, { id: string; jobId: string }>({
    mutationFn: async ({ id, jobId }) => {
      const res = await apiPost<{ data: DetachVisitResult }, { jobId: string }>(
        operationsClient,
        membershipEndpoints.detachRecurringServiceEvent(id),
        { jobId },
      );
      return res.data;
    },
    onSuccess: (result) => {
      invalidateQueue(queryClient);
      void queryClient.invalidateQueries({ queryKey: jobKeys.detail(result.jobId) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.appointments(result.jobId) });
    },
  });
}
